import dayjs from 'dayjs'
import type { EventFormValues } from 'components/events/EventForm/types'
import { initialFormValues } from 'components/events/EventForm/initialFormValues'
import type { ApplicationRow, DeskPrefillDeps } from './deskPrefill'
import { buildDeskPrefill, deskWeekly, editorPickPath } from './deskPrefill'

/**
 * Which fields Dominique edited before approving (DESK-07, D-09, IMG-04).
 *
 * "Edited" means the final value differs from what the editor produced, field
 * by field, over a closed list. The list is a literal copy of 0035's
 * `applications_desk_edited_fields_allowlist` CHECK
 * (mobile/supabase/migrations/20240101000035_desk.sql); the two must agree,
 * and a test reads the migration to prove they do. Anything else is refused by
 * the database (23514).
 *
 * The baseline is a pure transform of the application row,
 * `initialFormValues(buildDeskPrefill(row))`: the same function the form opens
 * with, never a snapshot of the live form. One difference: the baseline skips
 * the desk's own venue-by-name lookup, so a venue the editor did not resolve
 * counts as hers even when the desk pre-selected it. The form's async effects change
 * values after load (Pitfall 5); a snapshot would count their changes as hers.
 *
 * Rules:
 * - text is compared after trim + whitespace collapse, so whitespace-only
 *   changes are not edits;
 * - a bilingual pair (title, subtitle, description, price) counts when a side
 *   that was non-empty in the baseline changed. Filling the empty other side
 *   is translation, the desk's job, not an editor error. When the editor left
 *   both sides empty, filling either one counts: there was nothing to
 *   translate, the value is hers;
 * - price is compared as it would be saved (free = both sides empty);
 * - start / end compare at minute precision (`YYYY-MM-DD HH:mm`), null vs a
 *   date counts;
 * - venue counts when the selected venue changes or, on the custom venue, when
 *   its name or address does; city is `city_id`; organizer is the organizer
 *   id; category is the main tag;
 * - image counts when she uploaded or pasted a picture (the form's
 *   `uploaded` signal: an upload never changes `values.picture`, review
 *   AD-CR-03), or when the final picture's storage path differs from the
 *   editor's pick (`display_image_index`, not the desk swap). So a swap or
 *   an upload counts, and a swap-and-back or a plain re-crop does not;
 * - published, is_recommended, cancelled, the registration link and the
 *   secondary tags are not in the list and never count.
 *
 * Pure: no Supabase, no React, no i18n.
 */

export const EDITED_FIELDS = [
  'title',
  'subtitle',
  'description',
  'start',
  'end',
  'weekly',
  'city',
  'venue',
  'organizer',
  'price',
  'category',
  'image'
] as const

export type EditedField = (typeof EDITED_FIELDS)[number]

export type EditorBaselineDeps = DeskPrefillDeps & {
  /** The form language ('en' | 'pt-PT'): the main tag is read in it. */
  language: string
  /** The translated "custom venue" keyword the form uses (`venues.custom`). */
  customVenueKeyword: string
}

export type EditorBaseline = {
  values: EventFormValues
  weekly: boolean
  /** The storage path of the editor's pick, or null when it had none. */
  picturePath: string | null
  /** Carried so the diff knows when the venue is the free-text one. */
  customVenueKeyword: string
}

export type SubmittedValues = {
  values: EventFormValues
  weekly: boolean
  picturePath: string | null
  /** The picture going out is a file she uploaded or pasted. */
  uploaded: boolean
}

const STORAGE_MARKER = '/storage/v1/object/public/events/'

/**
 * The storage path inside the `events` bucket of a public URL (the part after
 * `/storage/v1/object/public/events/`, without a query string). A URL that is
 * not an events-bucket URL is returned trimmed as it is, so it still compares
 * as different from any stored path. Empty -> null.
 */
export const storagePathOf = (url: string | null | undefined): string | null => {
  const trimmed = (url ?? '').trim()
  if (!trimmed) return null
  const at = trimmed.indexOf(STORAGE_MARKER)
  if (at < 0) return trimmed
  const rawPath = trimmed.slice(at + STORAGE_MARKER.length).split(/[?#]/)[0]
  let decoded = rawPath
  try {
    decoded = decodeURIComponent(rawPath)
  } catch {
    decoded = rawPath
  }
  return decoded || null
}

/** The submitted side, from what the form hands to markProcessed. */
export const toSubmitted = (
  values: EventFormValues,
  weekly: boolean,
  uploaded: boolean = false
): SubmittedValues => ({
  values,
  weekly,
  picturePath: storagePathOf(values.picture),
  uploaded
})

export const buildEditorBaseline = (
  application: ApplicationRow,
  deps: EditorBaselineDeps
): EditorBaseline => {
  const { language, customVenueKeyword, ...prefillDeps } = deps
  const values = initialFormValues(buildDeskPrefill(application, prefillDeps, { matchVenue: false }), {
    tags: deps.tags.map((tag) => ({ id: tag.id, tag: tag.tag ?? '', tag_pt: tag.tag_pt ?? undefined })),
    language,
    isApplicationEdit: true,
    customVenueKeyword
  })
  return {
    values,
    weekly: deskWeekly(application),
    picturePath: editorPickPath(application, deps.rawItem),
    customVenueKeyword
  }
}

const norm = (value: string | null | undefined) => (value ?? '').replace(/\s+/g, ' ').trim()

const pairEdited = (
  baseline: [string | null | undefined, string | null | undefined],
  submitted: [string | null | undefined, string | null | undefined]
): boolean => {
  const [baseA, baseB] = baseline.map(norm)
  const [subA, subB] = submitted.map(norm)
  if (!baseA && !baseB) return !!subA || !!subB
  return (!!baseA && baseA !== subA) || (!!baseB && baseB !== subB)
}

const minute = (date: Date | null) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : null)

const savedPrice = (values: EventFormValues): [string, string] =>
  values.isFreeEvent ? ['', ''] : [values.price, values.price_pt]

const venueEdited = (baseline: EditorBaseline, submitted: EventFormValues): boolean => {
  const before = baseline.values
  if (norm(before.venue) !== norm(submitted.venue)) return true
  if (norm(submitted.venue) !== norm(baseline.customVenueKeyword)) return false
  return norm(before.venue_name) !== norm(submitted.venue_name) ||
    norm(before.venue_address) !== norm(submitted.venue_address)
}

export const computeEditedFields = (
  baseline: EditorBaseline,
  submitted: SubmittedValues
): EditedField[] => {
  const before = baseline.values
  const after = submitted.values
  const edited: Record<EditedField, boolean> = {
    title: pairEdited([before.title, before.title_pt], [after.title, after.title_pt]),
    subtitle: pairEdited([before.subTitle, before.subTitle_pt], [after.subTitle, after.subTitle_pt]),
    description: pairEdited([before.description, before.description_pt], [after.description, after.description_pt]),
    start: minute(before.startTime) !== minute(after.startTime),
    end: minute(before.finishTime) !== minute(after.finishTime),
    weekly: baseline.weekly !== submitted.weekly,
    city: (before.city_id ?? null) !== (after.city_id ?? null),
    venue: venueEdited(baseline, after),
    organizer: (before.user ?? null) !== (after.user ?? null),
    price: pairEdited(savedPrice(before), savedPrice(after)),
    category: norm(before.mainTag) !== norm(after.mainTag),
    image: submitted.uploaded || (baseline.picturePath ?? null) !== (submitted.picturePath ?? null)
  }
  return EDITED_FIELDS.filter((field) => edited[field])
}
